import React, { Fragment, useState } from 'react';
import _ from 'lodash';
import i18n from 'i18n';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import classNames from 'classnames/bind';
import styles from './limitationTableCell.module.scss';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { Modal } from 'components/common/Modal/Modal';
import { StoreLimitationGroups } from './StoreLimitationGroups';
import {
  DeliveryTargetGroup,
  DeliveryTargetUnit,
  withStatusOnlyTargets
} from './groupStoreLimitations';
import { DeliveryTargetStatus } from 'core/rtbCampaign/RtbCampaign';
import { buildStatusIndex } from './deliveryTargetStatus';

const cx = classNames.bind(styles);

type TaSummaryItem = SelectOptions & {
  // 門市／機台才有：依縣市分好的兩層結構。
  deliveryTargetGroups?: DeliveryTargetGroup[];
  deliveryTargetUnit?: DeliveryTargetUnit;
  deliveryTargetParentMunicipalities?: Set<string>;
  // 未經 join 的各筆值，明細裡逐筆以標籤呈現。沒帶的呼叫端退回整串 value。
  values?: string[];
};

interface TaSummaryData {
  [limitedTrafficType: string]: TaSummaryItem[];
}

type DeliveryTargetInfo = {
  validCount: number;
  totalCount: number;
  deliveryUnit?: DeliveryUnit;
  weatherFilteredCount: number;
  storeChangeCount: number;
  // ECOPD-5620: 門市超過鎖定上限（電子紙一台機台最多 10 檔活動）。
  overBindingLimitCount: number;
  // ECOPD-5691: 逐間門市的異常狀態。null＝後端沒回，清單維持中性色。
  deliveryTargetStatuses?: DeliveryTargetStatus[] | null;
};

export const LimitationTableCell: React.FC<{
  summaryData: TaSummaryData;
  l2ChannelId: string | number;
  deliveryTargetInfo?: DeliveryTargetInfo;
}> = ({ summaryData, l2ChannelId, deliveryTargetInfo }) => {
  const [showDetail, setShowDetail] = useState(false);
  const [selectedType, setSelectedType] = useState<string>();

  const handleLimitationDetail = () => {
    setShowDetail(!showDetail);
  };

  const limitedTrafficTypes = summaryData ? Object.keys(summaryData) : [];

  const unitKeyOf = (info: DeliveryTargetInfo) =>
    info.deliveryUnit === DeliveryUnit.DEVICE ? 'device' : 'store';

  const statusIndex = buildStatusIndex(
    deliveryTargetInfo?.deliveryTargetStatuses
  );

  /**
   * 選整個縣市時清單是從 ON 的選項樹展開的，已停用的門市因此一列都不會有 ——
   * 而那正是「門市異動」本身。狀態帶得動 value 與名字，缺的就由它補上。
   *
   * 只補在 include 那一組：狀態講的是這檔活動的投遞目標，排除／偏好那幾組列的是
   * 別的東西，補進去會把整份投遞目標灌進一份使用者只挑了幾間的清單。
   */
  /**
   * 整個縣市選進來的那幾組會缺門市：清單是靠只含上架版位的選項樹回查的，停用的
   * 門市因此一列都不會有 —— 而那正是統計卡在數的「門市異動」。狀態帶得動 value
   * 與名字，缺的就由它補上。
   *
   * 補在哪一組由「誰把那個縣市整個選進來」決定。狀態是一整份扁平清單、不分組，
   * 拿「誰沒列到它」判斷會把排除那一組的門市吸進投遞清單。
   */
  const groupsToRender = (data: TaSummaryItem) => {
    const groups = data.deliveryTargetGroups ?? [];
    const parents = data.deliveryTargetParentMunicipalities;
    return statusIndex && data.deliveryTargetUnit && parents?.size
      ? withStatusOnlyTargets(
          groups,
          Array.from(statusIndex.values()),
          data.deliveryTargetUnit,
          parents
        )
      : groups;
  };

  const renderLimitation = () => {
    return limitedTrafficTypes.map((limitedTrafficType) => {
      const types = summaryData[limitedTrafficType].map((data) => data.label);
      return (
        <Fragment key={limitedTrafficType}>
          <div
            className={styles.limitationTitle}
            key={`${limitedTrafficType}Title`}
          >
            <span className={styles[limitedTrafficType]}>
              {i18n.t(`limitation.${limitedTrafficType}`)}
            </span>
          </div>
          <div className={styles.colon}>{' : '}</div>
          <div
            className={styles.limitationValue}
            key={`${limitedTrafficType}Value`}
          >
            {types.join(', ')}
          </div>
        </Fragment>
      );
    });
  };

  /**
   * 每一列是「灰字 label 在左、值在右」。門市／機台那一列的值是整塊分組區，
   * 其餘限制項逐筆以標籤列出。
   */
  const renderRows = (limitedTrafficType: string) =>
    summaryData[limitedTrafficType].map((data) => (
      <div
        className={styles.limitationRow}
        key={`${limitedTrafficType}${data.label}Row`}
      >
        <div className={styles.rowLabel}>{data.label}</div>
        <div className={styles.rowValue}>
          {data.deliveryTargetGroups ? (
            <StoreLimitationGroups
              groups={groupsToRender(data)}
              statusIndex={statusIndex}
              unitKey={
                deliveryTargetInfo ? unitKeyOf(deliveryTargetInfo) : 'store'
              }
            />
          ) : (
            <div className={styles.valueList}>
              {(data.values ?? [data.value.toString()]).map((value, index) => (
                <div className={styles.valueTag} key={`${value}#${index}`}>
                  {value}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    ));

  const renderStatCards = (info: DeliveryTargetInfo) => {
    const unitKey = unitKeyOf(info);
    const ratioClass =
      info.validCount === 0
        ? styles.danger
        : info.validCount < info.totalCount
          ? styles.warning
          : undefined;

    return (
      <div className={styles.statCards}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>
            {i18n.t(
              `campaignList.labels.delivering${_.capitalize(unitKey)}Ratio`
            )}
          </div>
          <div className={styles.statValue}>
            <span className={cx('statPrimary', ratioClass)}>
              {info.validCount}
            </span>
            <span className={styles.statTotal}>{` / ${info.totalCount}`}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>
            {i18n.t('campaignList.labels.notDelivering')}
          </div>
          <div className={cx('statValue', 'statPrimary')}>
            {Math.max(0, info.totalCount - info.validCount)}
          </div>
        </div>
        {/* 0 不上色：這三格的顏色是在說「這個類別有異常」，0 沒有異常可說，
            長期掛著彩色的 0 會讓真的有異常時看不出差別。 */}
        <div className={styles.statCard}>
          <div className={styles.statLabel}>
            {i18n.t('campaignList.labels.weatherMismatch')}
          </div>
          <div
            className={cx('statValue', 'statPrimary', {
              warning: info.weatherFilteredCount > 0
            })}
          >
            {info.weatherFilteredCount}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>
            {i18n.t(`campaignList.labels.${unitKey}Change`)}
          </div>
          <div
            className={cx('statValue', 'statPrimary', {
              danger: info.storeChangeCount > 0
            })}
          >
            {info.storeChangeCount}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>
            {i18n.t('limitation.hints.overBindingLimit')}
          </div>
          <div
            className={cx('statValue', 'statPrimary', {
              danger: info.overBindingLimitCount > 0
            })}
          >
            {info.overBindingLimitCount}
          </div>
        </div>
      </div>
    );
  };

  const renderLimitationModal = () => {
    // 選過的頁籤若隨著資料更新而消失，退回第一個，不要留在空頁籤上。
    const activeType =
      selectedType && limitedTrafficTypes.includes(selectedType)
        ? selectedType
        : limitedTrafficTypes[0];

    return (
      <Modal
        className={styles.limitationDetailModal}
        title={i18n.t('campaignList.headers.limitationColumn')}
        secondaryButton={{
          title: i18n.t('common.buttons.close'),
          callback: handleLimitationDetail
        }}
        dismiss={handleLimitationDetail}
      >
        <div className={styles.limitationModalBody}>
          {deliveryTargetInfo && renderStatCards(deliveryTargetInfo)}
          <div className={styles.tabs} role='tablist'>
            {limitedTrafficTypes.map((limitedTrafficType) => (
              <button
                type='button'
                role='tab'
                aria-selected={limitedTrafficType === activeType}
                key={limitedTrafficType}
                className={cx('tab', limitedTrafficType, {
                  activeTab: limitedTrafficType === activeType
                })}
                onClick={() => setSelectedType(limitedTrafficType)}
              >
                {i18n.t(`limitation.${limitedTrafficType}`)}
              </button>
            ))}
          </div>
          {/* 統計卡與頁籤固定，只有這一區捲動。 */}
          <div className={styles.limitationRows}>
            {activeType && renderRows(activeType)}
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className={styles.tableCell}>
      {showDetail && renderLimitationModal()}
      {limitedTrafficTypes.length > 0 ? (
        <div className={styles.limitationContent}>{renderLimitation()}</div>
      ) : (
        i18n.t('campaignList.labels.emptyContent')
      )}
      {Object.keys(summaryData).length > 0 && (
        <div className={styles.detailBtn}>
          <IconWithTooltip
            icon={faEye}
            tooltipProps={{
              id: `campaignListLimitationDetailTip-${l2ChannelId}`,
              tooltip: i18n.t('campaignList.labels.limitationDetail')
            }}
            onClick={handleLimitationDetail}
          />
        </div>
      )}
    </div>
  );
};
